import React, { useState, useEffect, useCallback } from 'react';

const TournamentManager = () => {
  const [tournaments, setTournaments] = useState([]);
  const [events, setEvents] = useState([]);
  const [structures, setStructures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState('');

  const getApiUrl = () => {
    const hostname = window.location.hostname;
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return 'http://localhost:3000';
    } else if (hostname === '10.0.0.28') {
      return `https://${hostname}/api`;
    } else {
      return `https://${hostname}/api`;
    }
  };

  const loadData = useCallback(async () => {
    try {
      // Load all tournaments
      const tournamentsResponse = await fetch(`${getApiUrl()}/tournaments`);
      const tournamentsData = await tournamentsResponse.json();
      setTournaments(tournamentsData);

      // Load all events
      const eventsResponse = await fetch(`${getApiUrl()}/events`);
      const eventsData = await eventsResponse.json();
      setEvents(eventsData);

      // Load blind structures
      const structuresResponse = await fetch(`${getApiUrl()}/blind-structures`);
      const structuresData = await structuresResponse.json();
      setStructures(structuresData);

      setLoading(false);
    } catch (error) {
      console.error('Error loading data:', error);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const createTournament = async () => {
    if (!selectedEvent) {
      alert('Please select an event');
      return;
    }

    try {
      const response = await fetch(`${getApiUrl()}/tournaments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ event_id: parseInt(selectedEvent) })
      });

      if (response.ok) {
        setShowCreateForm(false);
        setSelectedEvent('');
        loadData();
        alert('Tournament created successfully!');
      }
    } catch (error) {
      console.error('Error creating tournament:', error);
    }
  };

  const deleteTournament = async (tournamentId) => {
    if (!window.confirm('Are you sure you want to delete this tournament? This cannot be undone.')) {
      return;
    }

    try {
      const response = await fetch(`${getApiUrl()}/tournaments/${tournamentId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        loadData();
        alert('Tournament deleted successfully!');
      }
    } catch (error) {
      console.error('Error deleting tournament:', error);
    }
  };

  const updateTournamentStructure = async (tournamentId, structureId) => {
    try {
      const response = await fetch(`${getApiUrl()}/tournaments/${tournamentId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ structure_id: parseInt(structureId) })
      });

      if (response.ok) {
        loadData();
        alert('Tournament structure updated!');
      }
    } catch (error) {
      console.error('Error updating structure:', error);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'running': return '#28a745';
      case 'paused': return '#ffc107';
      case 'break': return '#17a2b8';
      case 'finished': return '#6c757d';
      case 'registration': return '#007bff';
      default: return '#6c757d';
    }
  };

  if (loading) {
    return <div className="container">Loading tournaments...</div>;
  }

  return (
    <div className="container">
      <h1>Tournament Manager</h1>
      
      {/* Create New Tournament */}
      <div className="card">
        <h3>Tournament Management</h3>
        <button 
          className="btn btn-success" 
          onClick={() => setShowCreateForm(!showCreateForm)}
        >
          {showCreateForm ? 'Cancel' : 'Create New Tournament'}
        </button>

        {showCreateForm && (
          <div style={{ marginTop: '20px', padding: '20px', background: '#f8f9fa', borderRadius: '5px' }}>
            <h4>Create Tournament from Event</h4>
            <div className="form-group">
              <label>Select Event:</label>
              <select 
                className="form-control"
                value={selectedEvent}
                onChange={(e) => setSelectedEvent(e.target.value)}
              >
                <option value="">Choose an event...</option>
                {events.map(event => (
                  <option key={event.id} value={event.id}>
                    {event.name} - {event.game_type} (${event.buy_in_amount})
                  </option>
                ))}
              </select>
            </div>
            <button className="btn btn-success" onClick={createTournament}>
              Create Tournament
            </button>
          </div>
        )}
      </div>

      {/* Existing Tournaments */}
      <div className="card">
        <h3>Existing Tournaments ({tournaments.length})</h3>
        {tournaments.length === 0 ? (
          <p>No tournaments found. Create your first tournament above.</p>
        ) : (
          <div>
            {tournaments.map(tournament => (
              <div key={tournament.id} className="card" style={{ margin: '10px 0' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <h4>
                      {tournament.event_name || `Tournament ${tournament.id}`}
                      <span style={{ 
                        color: getStatusColor(tournament.status),
                        marginLeft: '10px',
                        fontSize: '16px',
                        fontWeight: 'bold'
                      }}>
                        [{tournament.status.toUpperCase()}]
                      </span>
                    </h4>
                    <div style={{ color: '#666' }}>
                      <div>Game: {tournament.game_type} | Level: {tournament.current_level} | Players: {tournament.players_remaining}</div>
                      <div>Dealer Code: {tournament.dealer_code} | Created: {new Date(tournament.created_at).toLocaleString()}</div>
                    </div>
                  </div>
                  
                  <div style={{ display: 'flex', gap: '10px', flexDirection: 'column' }}>
                    <div>
                      <a href={`/clock/${tournament.id}`} className="btn" target="_blank" rel="noopener noreferrer">
                        View Clock
                      </a>
                      <a href={`/control/${tournament.id}`} className="btn" target="_blank" rel="noopener noreferrer">
                        Control Panel
                      </a>
                    </div>
                    <div>
                      <a href={`/admin/tournament/${tournament.id}/settings`} className="btn">
                        Settings
                      </a>
                      <a href={`/admin/tournament/${tournament.id}/registrations`} className="btn">
                        Registration
                      </a>
                      <a href={`/admin/tournament/${tournament.id}/tables`} className="btn" target="_blank" rel="noopener noreferrer">
  🎲 Table Management
</a>
                    </div>
                    <div>
                      <select 
                        onChange={(e) => {
                          if (e.target.value) {
                            updateTournamentStructure(tournament.id, e.target.value);
                            e.target.value = '';
                          }
                        }}
                        className="form-control"
                        style={{ fontSize: '12px' }}
                      >
                        <option value="">Change Structure...</option>
                        {structures.map(structure => (
                          <option key={structure.id} value={structure.id}>
                            {structure.name}
                          </option>
                        ))}
                      </select>
                      <button 
                        className="btn btn-danger"
                        onClick={() => deleteTournament(tournament.id)}
                        style={{ fontSize: '12px', marginTop: '5px' }}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default TournamentManager;
